/**
 * Policy Terms — reference-column inputs (right side of the grid).
 * Per Occurrence / Aggregate are dropdowns with Dario's most-common
 * values + a Custom entry (OR-1331; presets confirmed in OR-1330's
 * design comment). They ship preselected with the standard values from
 * the original Figma ($1M / $3M) — a default-value confirmation is
 * still open with design (OR-1330). (The "Dec. SIR" row was removed per
 * Henry/Linda jun-19: the Primary Premium Walk "Declared SIR" is the
 * single source for `declared_sir`.)
 */
import { FileCheck02 } from "@untitledui/icons";
import { FormGroup } from "../../../../components/form-group";
import { MoneySelect } from "../../../../components/money-select";
import { PER_OCCURRENCE_PRESETS, POLICY_AGGREGATE_PRESETS, clampMoneyToWire } from "../premium-selection-types";
import type { PremiumSelectionInputs } from "../../../../domain/pricing";
import { CardRows, FieldRow } from "../components/field-row";
import { useBlurFlash } from "../components/use-blur-flash";
import type { InputKey } from "../use-premium-selection";

const TERMS: Array<{ key: InputKey; label: string; presets: number[]; dataAutotest: string }> = [
    {
        key: "perOccurrenceLimit",
        label: "Per Occurrence Limit",
        presets: PER_OCCURRENCE_PRESETS,
        dataAutotest: "premium-selection-policy-terms-per-occurrence-limit-row",
    },
    {
        key: "policyAggregateLimit",
        label: "Policy Aggregate Limit",
        presets: POLICY_AGGREGATE_PRESETS,
        dataAutotest: "premium-selection-policy-terms-policy-aggregate-limit-row",
    },
];

interface PolicyTermsCardProps {
    inputs: PremiumSelectionInputs;
    setInput: (key: InputKey, value: string) => void;
}

export function PolicyTermsCard({ inputs, setInput }: PolicyTermsCardProps) {
    return (
        <FormGroup title="Policy Terms" icon={FileCheck02} data-autotest="premium-selection-policy-terms-card">
            <CardRows>
                {TERMS.map(({ key, label, presets, dataAutotest }) => (
                    <TermRow
                        key={key}
                        inputKey={key}
                        label={label}
                        presets={presets}
                        value={inputs[key]}
                        setInput={setInput}
                        dataAutotest={dataAutotest}
                    />
                ))}
            </CardRows>
        </FormGroup>
    );
}

function TermRow({
    inputKey,
    label,
    presets,
    value,
    setInput,
    dataAutotest,
}: {
    inputKey: InputKey;
    label: string;
    presets: number[];
    value: string;
    setInput: (key: InputKey, value: string) => void;
    dataAutotest: string;
}) {
    const { flashKey, flash } = useBlurFlash();
    return (
        <FieldRow label={label} flashKey={flashKey} controlClassName="w-44" data-autotest={dataAutotest}>
            <MoneySelect
                aria-label={label}
                value={value}
                presets={presets}
                onChange={(next) => {
                    // Keep the PUT inside the wire's Numeric(14,2) range.
                    setInput(inputKey, clampMoneyToWire(next));
                    flash();
                }}
            />
        </FieldRow>
    );
}
